import React, { useMemo, useState } from "react";
import * as Icons from "lucide-react";
import GlassCard from "@/components/common/GlassCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const CODES = [301, 302, 307, 308];

function normalizePath(value) {
  const raw = (value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return raw.startsWith("/") ? raw : `/${raw}`;
}

export default function SEORedirectManager({ redirects, onAdd, onDelete, onImport, onExport }) {
  const [form, setForm] = useState({ from: "", to: "", code: 301 });
  const [q, setQ] = useState("");
  const [pending, setPending] = useState(null);
  const filtered = (redirects || []).filter((r) => !q || `${r.from} ${r.to}`.toLowerCase().includes(q.toLowerCase()));
  const issues = useMemo(() => {
    const src = normalizePath(form.from);
    const dest = normalizePath(form.to);
    const out = [];
    if (src && dest && src === dest) out.push("From and to cannot be the same.");
    if (src.startsWith("http://") || dest.startsWith("http://")) out.push("Use https://");
    return out;
  }, [form]);
  return (
    <GlassCard className="p-6">
      <p className="text-xs text-white/50 mb-4">Redirects are applied on the public site router via `/api/public/redirect`. Hits increment only when a visitor path matches. CSV: from,to,code</p>
      <div className="flex gap-2 mb-4">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search redirects" className="bg-black/20 border-white/10 text-white max-w-xs" />
        <Button variant="ghost" className="text-white/70" onClick={onExport}>Export</Button>
        <label className="text-xs text-white/60 self-center cursor-pointer rounded-md border border-white/10 px-3 py-2 hover:bg-white/5">
          Import CSV
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            onImport(await file.text());
            e.target.value = "";
          }} />
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_120px_auto] gap-2 mb-4 items-end">
        <div><Label className="text-xs text-white/50">From</Label><Input value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} className="bg-black/20 border-white/10 text-white mt-1" placeholder="/old" /></div>
        <div><Label className="text-xs text-white/50">To</Label><Input value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} className="bg-black/20 border-white/10 text-white mt-1" placeholder="/new" /></div>
        <div><Label className="text-xs text-white/50">Code</Label><select value={form.code} onChange={(e) => setForm({ ...form, code: Number(e.target.value) })} className="mt-1 w-full h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2">{CODES.map((c) => <option key={c}>{c}</option>)}</select></div>
        <Button onClick={() => onAdd({ ...form, from: normalizePath(form.from), to: normalizePath(form.to) })} disabled={issues.length > 0 || !form.from || !form.to} className="btn-gradient text-white border-0">Add</Button>
      </div>
      {issues.map((i) => <p key={i} className="text-xs text-amber-300">{i}</p>)}
      <div className="space-y-2 mt-4">
        {filtered.map((r) => (
          <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg glass text-sm">
            <span className="font-mono text-white/90">{r.from}</span>
            <Icons.ArrowRight className="w-4 h-4 text-white/30" />
            <span className="font-mono text-white/90">{r.to}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5">{r.code}</span>
            {r.chain > 1 && <span className="text-[10px] text-amber-300">chain {r.chain}</span>}
            <span className="text-[10px] text-white/40">{r.hits || 0} hits</span>
            {r.last_accessed && <span className="text-[10px] text-white/30">{String(r.last_accessed).slice(0, 16)}</span>}
            <button className="ml-auto text-white/40" onClick={() => setPending(r)} aria-label="Delete redirect"><Icons.Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-center text-white/40 py-6">No redirects yet.</p>}
      </div>
      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent className="glass-strong border-white/10 text-white">
          <AlertDialogHeader><AlertDialogTitle>Delete this redirect?</AlertDialogTitle><AlertDialogDescription className="text-white/60 font-mono">{pending?.from} → {pending?.to}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="text-white">Cancel</AlertDialogCancel><AlertDialogAction onClick={() => { onDelete(pending); setPending(null); }} className="bg-red-600 text-white">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </GlassCard>
  );
}
